import React, { useEffect, useRef, useState, memo, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import DOMPurify from 'dompurify';

interface OptimizedMathRendererProps {
  content: string;
  inline?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onError?: (error: string) => void;
  priority?: 'high' | 'normal' | 'low';
}

/**
 * 优化的数学公式渲染器 - 专门解决弹窗中的渲染性能问题
 * 
 * 核心优化：
 * - 使用DOM直接渲染HTML，然后用MathJax处理其中的数学公式
 * - 支持渲染优先级，弹窗中的内容使用高优先级
 * - 延迟渲染非关键内容，提升首次加载速度
 * - 智能检测数学公式，避免不必要的MathJax处理
 * - 正确处理HTML实体和标签
 */
const OptimizedMathRenderer: React.FC<OptimizedMathRendererProps> = memo(({
  content,
  inline = false,
  className = '',
  style = {},
  onError,
  priority = 'normal'
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement | HTMLSpanElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 根据优先级决定渲染时机
  useEffect(() => {
    if (!content || typeof content !== 'string') {
      return;
    }

    const delay = priority === 'high' ? 0 : priority === 'normal' ? 100 : 300;
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setShouldRender(true);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [content, priority]);

  // 检测是否包含数学公式
  const hasMathContent = useMemo(() => {
    if (!content) return false;
    return /\$|\\\(|\\\[|\\begin\{|\\ce\{|\\mathrm\{/.test(content);
  }, [content]);

  // HTML实体解码
  const decodeHtmlEntities = useCallback((text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }, []);

  // 安全清理HTML内容
  const sanitizeHtml = useCallback((html: string): string => {
    // 先解码HTML实体
    const decodedHtml = decodeHtmlEntities(html);
    
    return DOMPurify.sanitize(decodedHtml, {
      ALLOWED_TAGS: [
        'p', 'div', 'span', 'br', 'strong', 'em', 'u', 'sub', 'sup',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'img', 'audio', 'video', 'source',
        'table', 'tr', 'td', 'th', 'thead', 'tbody'
      ],
      ALLOWED_ATTR: [
        'src', 'alt', 'title', 'href', 'target',
        'style', 'class', 'id',
        'controls', 'autoplay', 'loop', 'type'
      ],
      KEEP_CONTENT: true
    });
  }, [decodeHtmlEntities]);

  // 使用DOM直接渲染，然后处理数学公式
  useEffect(() => {
    if (!shouldRender || !containerRef.current || hasError || !isMountedRef.current) {
      return;
    }

    const container = containerRef.current;
    
    try {
      // 清理HTML内容并设置到DOM
      const cleanedHtml = sanitizeHtml(content);
      container.innerHTML = cleanedHtml;
      
      // 如果包含数学公式，使用MathJax处理
      if (hasMathContent && window.MathJax && window.MathJax.typesetPromise) {
        // 添加超时保护
        const timeoutId = setTimeout(() => {
          if (isMountedRef.current) {
            console.warn('MathJax渲染超时，使用原始内容');
          }
        }, 10000);
        
        window.MathJax.typesetPromise([container])
          .then(() => {
            clearTimeout(timeoutId);
          })
          .catch((error: any) => {
            clearTimeout(timeoutId);
            if (isMountedRef.current) {
              const errorMsg = `数学公式渲染失败: ${error?.message || String(error)}`;
              console.warn(errorMsg);
              onError?.(errorMsg);
              // 不设置hasError，保持HTML内容显示
            }
          });
      }
      
    } catch (error) {
      if (isMountedRef.current) {
        const errorMsg = `内容渲染失败: ${error instanceof Error ? error.message : String(error)}`;
        console.warn(errorMsg);
        setHasError(true);
        onError?.(errorMsg);
        
        // 降级显示原始文本
        container.textContent = content;
      }
    }
  }, [shouldRender, content, hasMathContent, sanitizeHtml, onError]);

  // 如果没有内容或组件已卸载，返回null
  if (!content || !shouldRender || !isMountedRef.current) {
    return null;
  }

  // 如果发生严重错误，显示原始内容
  if (hasError) {
    return (
      <Box
        component={inline ? 'span' : 'div'}
        className={`optimized-math-renderer error ${className}`}
        style={{
          display: inline ? 'inline' : 'block',
          color: '#666',
          fontStyle: 'italic',
          fontSize: '14px',
          ...style
        }}
      >
        {content}
      </Box>
    );
  }

  // 使用ref的DOM容器
  return (
    <Box
      ref={containerRef as any}
      component={inline ? 'span' : 'div'}
      className={`optimized-math-renderer ${className}`}
      style={{
        display: inline ? 'inline' : 'block',
        lineHeight: '1.6',
        fontSize: '14px',
        ...style
      }}
    />
  );
});

// 设置displayName便于调试
OptimizedMathRenderer.displayName = 'OptimizedMathRenderer';

export default OptimizedMathRenderer;